import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { AwardIcon, BookOpenIcon, BriefcaseIcon, GlobeIcon, MapPinIcon, MountainSnowIcon, ShieldCheckIcon, StarIcon, HeartIcon, LanguagesIcon } from 'lucide-react';
interface GuidePageProps {
  guideKey: string;
  onNavigate: (page: string) => void;
}
interface CertificateItem {
  title: string;
  issuer: string;
  year: string;
  image: string;
}
interface AchievementItem {
  title: string;
  description: string;
}
interface GuideConfig {
  name: string;
  role: string;
  photo: string;
  heroImage: string;
  location: string;
  languages: string[];
  specialties: string[];
  yearsExperience: number;
  bio: string[];
  education: {
    degree: string;
    institution: string;
    year: string;
  }[];
  experience: {
    role: string;
    company: string;
    period: string;
    description: string;
  }[];
  certificates: CertificateItem[];
  achievements: AchievementItem[];
}
const guides: Record<string, GuideConfig> = {
  'guide-rajesh': {
    name: 'Rajesh Shrestha',
    role: 'Founder & Managing Director',
    photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80',
    heroImage: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=1920&q=80',
    location: 'Kathmandu, Nepal',
    languages: ['English', 'Nepali', 'Hindi', 'Tibetan'],
    specialties: ['Luxury Travel Design', 'High-Altitude Expeditions', 'Cultural Tourism', 'Business Strategy'],
    yearsExperience: 22,
    bio: ['Rajesh Shrestha is the visionary founder of Nepal Luxury Travel, a company born from his lifelong passion for the Himalayas and his belief that adventure and luxury are not mutually exclusive. With over two decades of experience in the travel industry and three successful Everest summits under his belt, Rajesh brings a rare combination of mountaineering expertise and hospitality excellence to every journey he designs.', 'Growing up in a small village in the Solukhumbu district — the gateway to Everest — Rajesh developed an intimate understanding of the mountains, the Sherpa culture, and the transformative power of travel. After completing his education in hospitality management, he spent several years working with international expedition companies before founding Nepal Luxury Travel in 2010.', "Under his leadership, the company has grown from a small trekking operation to Nepal's most respected luxury travel brand, serving over 5,000 guests from more than 40 countries. Rajesh personally oversees the design of every itinerary and maintains direct relationships with Nepal's finest hotels, lodges, and airlines to ensure his guests receive the highest level of service."],
    education: [{
      degree: 'MBA in Tourism & Hospitality Management',
      institution: 'Tribhuvan University, Kathmandu',
      year: '2005'
    }, {
      degree: 'Advanced Mountaineering Course',
      institution: 'Nepal Mountaineering Association',
      year: '2002'
    }, {
      degree: 'Bachelor of Business Administration',
      institution: 'Kathmandu University',
      year: '2001'
    }],
    experience: [{
      role: 'Founder & Managing Director',
      company: 'Nepal Luxury Travel',
      period: '2010 – Present',
      description: "Built Nepal's premier luxury travel company from the ground up, serving 5,000+ guests across 50+ curated experiences."
    }, {
      role: 'Senior Expedition Manager',
      company: 'Himalayan Expeditions International',
      period: '2005 – 2010',
      description: 'Managed high-altitude expeditions to Everest, Cho Oyu, and Ama Dablam for international clients.'
    }, {
      role: 'Trek Leader & Guide',
      company: 'Adventure Nepal Treks',
      period: '2001 – 2005',
      description: 'Led over 100 trekking groups through the Everest, Annapurna, and Langtang regions.'
    }],
    certificates: [{
      title: 'UIAGM Mountain Guide License',
      issuer: 'International Federation of Mountain Guides',
      year: '2004',
      image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&q=80'
    }, {
      title: 'Wilderness First Responder',
      issuer: 'NOLS Wilderness Medicine',
      year: '2006',
      image: 'https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=600&q=80'
    }, {
      title: 'Sustainable Tourism Certificate',
      issuer: 'Global Sustainable Tourism Council',
      year: '2018',
      image: 'https://images.unsplash.com/photo-1486911278844-a81c5267e227?w=600&q=80'
    }, {
      title: 'Luxury Travel Advisor',
      issuer: 'Virtuoso Travel Network',
      year: '2020',
      image: 'https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=600&q=80'
    }],
    achievements: [{
      title: '3× Everest Summit',
      description: 'Successfully summited Mount Everest (8,849m) in 2003, 2007, and 2012.'
    }, {
      title: 'Nepal Tourism Award',
      description: "Received the Nepal Tourism Board's Excellence Award for Luxury Tourism in 2019."
    }, {
      title: '5,000+ Happy Guests',
      description: 'Served travelers from over 40 countries with a 98% satisfaction rating.'
    }, {
      title: 'Community Impact',
      description: 'Established scholarship programs for 50+ children in Solukhumbu district.'
    }]
  },
  'guide-sita': {
    name: 'Sita Gurung',
    role: 'Head of Operations',
    photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&q=80',
    heroImage: 'https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=1920&q=80',
    location: 'Pokhara, Nepal',
    languages: ['English', 'Nepali', 'French', 'Japanese'],
    specialties: ['Operations Management', 'Guest Experience', 'Cultural Programs', 'Team Leadership'],
    yearsExperience: 15,
    bio: ['Sita Gurung is the operational backbone of Nepal Luxury Travel, ensuring every journey runs with clockwork precision while maintaining the warmth and personal touch that defines the company. With 15 years of experience in luxury hospitality and a natural gift for languages — she speaks four fluently — Sita bridges the gap between international travelers and authentic Nepali experiences.', "Born in Pokhara, the gateway to the Annapurna range, Sita grew up surrounded by the mountains and the vibrant tourism culture of Nepal's lakeside city. After studying hospitality management in Kathmandu and completing an advanced program in Switzerland, she returned to Nepal determined to elevate the standard of service in the country's tourism industry.", "As Head of Operations, Sita manages a team of 30+ staff including guides, drivers, hotel liaisons, and support personnel. She personally reviews every guest itinerary, coordinates with partner hotels and airlines, and handles VIP arrangements. Her attention to detail is legendary — from remembering a guest's dietary preferences to arranging surprise birthday celebrations at 4,000 meters."],
    education: [{
      degree: 'Diploma in Hospitality Management',
      institution: 'École hôtelière de Lausanne, Switzerland',
      year: '2011'
    }, {
      degree: 'Bachelor of Tourism Studies',
      institution: 'Tribhuvan University, Kathmandu',
      year: '2009'
    }, {
      degree: 'Japanese Language Proficiency (JLPT N2)',
      institution: 'Japan Foundation, Kathmandu',
      year: '2013'
    }],
    experience: [{
      role: 'Head of Operations',
      company: 'Nepal Luxury Travel',
      period: '2014 – Present',
      description: 'Manages all operational aspects including team coordination, partner relationships, and guest experience quality.'
    }, {
      role: 'Guest Relations Manager',
      company: "Dwarika's Hotel, Kathmandu",
      period: '2011 – 2014',
      description: "Managed VIP guest services at Nepal's most prestigious heritage hotel."
    }, {
      role: 'Tourism Coordinator',
      company: 'Nepal Tourism Board',
      period: '2009 – 2011',
      description: 'Coordinated international tourism campaigns and visitor experience programs.'
    }],
    certificates: [{
      title: 'Certified Hospitality Administrator',
      issuer: 'American Hotel & Lodging Association',
      year: '2012',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80'
    }, {
      title: 'Crisis Management in Tourism',
      issuer: 'World Tourism Organization (UNWTO)',
      year: '2016',
      image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80'
    }, {
      title: 'Cultural Heritage Tourism',
      issuer: 'UNESCO Nepal',
      year: '2019',
      image: 'https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=600&q=80'
    }, {
      title: 'Advanced First Aid',
      issuer: 'Nepal Red Cross Society',
      year: '2021',
      image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80'
    }],
    achievements: [{
      title: 'Service Excellence Award',
      description: 'Recognized by Nepal Tourism Board for outstanding guest service standards in 2022.'
    }, {
      title: '4 Languages Fluent',
      description: 'Fluent in English, Nepali, French, and Japanese — enabling seamless communication with global travelers.'
    }, {
      title: '98% Guest Satisfaction',
      description: 'Maintained a 98% positive feedback rate across 3,000+ guest interactions.'
    }, {
      title: 'Women in Tourism Leader',
      description: 'Featured in Nepal\'s "Women Leading Tourism" initiative as a role model for young professionals.'
    }]
  },
  'guide-pemba': {
    name: 'Pemba Sherpa',
    role: 'Lead Trek Guide',
    photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&q=80',
    heroImage: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=1920&q=80',
    location: 'Namche Bazaar, Solukhumbu',
    languages: ['English', 'Nepali', 'Sherpa', 'German'],
    specialties: ['High-Altitude Trekking', 'Mountaineering', 'Wilderness Medicine', 'Sherpa Culture'],
    yearsExperience: 15,
    bio: ["Pemba Sherpa is one of Nepal's most experienced and respected trekking guides, with 15 years of guiding experience across every major trekking route in the Himalayas. Born and raised in Namche Bazaar — the legendary Sherpa capital in the heart of the Everest region — Pemba carries the mountains in his blood. His grandfather was among the first Sherpa porters to support early Everest expeditions in the 1950s.", "What sets Pemba apart is his rare combination of technical mountaineering skill, deep cultural knowledge, and genuine warmth. He doesn't just guide you through the mountains — he introduces you to his homeland, sharing stories of Sherpa traditions, pointing out medicinal plants, and explaining the significance of every monastery and prayer flag along the trail.", 'Pemba holds international mountaineering certifications and is trained in high-altitude medicine, carrying satellite communication devices and supplemental oxygen on every trek. His safety record is impeccable — in 15 years and over 200 expeditions, he has never had a serious altitude-related incident among his clients. He attributes this to careful acclimatization planning, constant health monitoring, and knowing when to turn back.'],
    education: [{
      degree: 'Advanced Mountaineering Course',
      institution: 'Nepal Mountaineering Association',
      year: '2010'
    }, {
      degree: 'Wilderness Emergency Medical Technician',
      institution: 'NOLS Wilderness Medicine',
      year: '2012'
    }, {
      degree: 'Tourism & Trekking Guide Training',
      institution: 'Nepal Academy of Tourism & Hotel Management',
      year: '2008'
    }],
    experience: [{
      role: 'Lead Trek Guide',
      company: 'Nepal Luxury Travel',
      period: '2012 – Present',
      description: 'Leads all premium Everest Base Camp, Annapurna, and Upper Mustang expeditions with a perfect safety record.'
    }, {
      role: 'Expedition Guide',
      company: 'Himalayan Experience',
      period: '2009 – 2012',
      description: 'Guided international clients on Everest, Cho Oyu, and Island Peak expeditions.'
    }, {
      role: 'Assistant Guide',
      company: 'Khumbu Trekking',
      period: '2008 – 2009',
      description: 'Assisted senior guides on Everest Base Camp and Gokyo Lakes treks.'
    }],
    certificates: [{
      title: 'UIAGM Trekking Guide License',
      issuer: 'International Federation of Mountain Guides',
      year: '2011',
      image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&q=80'
    }, {
      title: 'Wilderness First Responder',
      issuer: 'NOLS Wilderness Medicine',
      year: '2012',
      image: 'https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=600&q=80'
    }, {
      title: 'High Altitude Medicine Certificate',
      issuer: 'Himalayan Rescue Association',
      year: '2014',
      image: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=600&q=80'
    }, {
      title: 'Avalanche Safety Level 2',
      issuer: 'Nepal Mountaineering Association',
      year: '2016',
      image: 'https://images.unsplash.com/photo-1486911278844-a81c5267e227?w=600&q=80'
    }],
    achievements: [{
      title: '5× Everest Summit',
      description: 'Successfully summited Mount Everest five times, including guiding clients to the summit in 2018.'
    }, {
      title: '200+ Expeditions Led',
      description: 'Led over 200 trekking and mountaineering expeditions with a perfect safety record.'
    }, {
      title: 'Island Peak Record',
      description: 'Guided the fastest luxury group ascent of Island Peak (6,189m) in 2021.'
    }, {
      title: 'Best Guide Award',
      description: 'Named "Guide of the Year" by the Trekking Agencies\' Association of Nepal (TAAN) in 2020.'
    }]
  },
  'guide-maya': {
    name: 'Maya Tamang',
    role: 'Guest Relations Manager',
    photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&q=80',
    heroImage: 'https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=1920&q=80',
    location: 'Kathmandu, Nepal',
    languages: ['English', 'Nepali', 'Mandarin', 'Korean'],
    specialties: ['Guest Relations', 'Cultural Experiences', 'Event Planning', 'Wellness Tourism'],
    yearsExperience: 10,
    bio: ["Maya Tamang is the heart and soul of Nepal Luxury Travel's guest experience. As Guest Relations Manager, she is often the first person travelers interact with and the last to say goodbye — and she makes both moments unforgettable. Her natural warmth, cultural sensitivity, and meticulous attention to detail ensure that every guest feels not just welcomed, but truly cared for.", "Maya comes from the Tamang community, one of Nepal's largest indigenous groups with a rich cultural heritage rooted in Tibetan Buddhism. This background gives her a deep understanding of Nepal's diverse cultures and traditions, which she weaves into every guest interaction — from arranging private monastery visits to organizing authentic cooking classes with local families.", "Before joining Nepal Luxury Travel, Maya honed her hospitality skills at some of Asia's finest hotels, including the Hyatt Regency Kathmandu and the Pavilions Himalayas in Pokhara. She holds certifications in luxury hospitality management and wellness tourism, and has a particular talent for creating personalized cultural experiences that go far beyond typical tourist activities."],
    education: [{
      degree: 'Master of Hospitality Management',
      institution: 'Kathmandu University School of Management',
      year: '2016'
    }, {
      degree: 'Bachelor of Tourism & Travel Management',
      institution: 'Tribhuvan University',
      year: '2014'
    }, {
      degree: 'Mandarin Language Certificate (HSK 4)',
      institution: 'Confucius Institute, Kathmandu',
      year: '2018'
    }],
    experience: [{
      role: 'Guest Relations Manager',
      company: 'Nepal Luxury Travel',
      period: '2018 – Present',
      description: 'Manages all guest communications, personalized itinerary customization, and VIP experience coordination.'
    }, {
      role: 'Guest Experience Coordinator',
      company: 'Pavilions Himalayas, Pokhara',
      period: '2016 – 2018',
      description: "Coordinated luxury guest experiences at Nepal's premier eco-resort."
    }, {
      role: 'Front Office Executive',
      company: 'Hyatt Regency Kathmandu',
      period: '2014 – 2016',
      description: 'Managed front desk operations and VIP guest services at a 5-star international hotel.'
    }],
    certificates: [{
      title: 'Certified Guest Experience Professional',
      issuer: 'Cornell University (Online)',
      year: '2019',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80'
    }, {
      title: 'Wellness Tourism Specialist',
      issuer: 'Global Wellness Institute',
      year: '2020',
      image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80'
    }, {
      title: 'Cultural Heritage Guide',
      issuer: 'Nepal Tourism Board',
      year: '2017',
      image: 'https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=600&q=80'
    }, {
      title: 'Advanced First Aid & CPR',
      issuer: 'Nepal Red Cross Society',
      year: '2021',
      image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80'
    }],
    achievements: [{
      title: 'Guest Satisfaction Champion',
      description: 'Achieved the highest guest satisfaction scores in company history — 99.2% positive feedback in 2023.'
    }, {
      title: 'Cultural Ambassador',
      description: 'Designed 15+ unique cultural immersion experiences now offered as signature Nepal Luxury Travel programs.'
    }, {
      title: 'Rising Star Award',
      description: 'Named "Rising Star in Hospitality" by Nepal Hotel Association in 2022.'
    }, {
      title: 'Community Outreach',
      description: "Leads the company's women empowerment initiative, training 30+ women in tourism hospitality."
    }]
  }
};
function GuideHero({
  config,
  onNavigate
}: {
  config: GuideConfig;
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full min-h-[50vh] flex items-end overflow-hidden" data-id="element-1257">
      <div className="absolute inset-0" data-id="element-1258">
        <img src={config.heroImage} alt={`${config.name} — ${config.role}`} className="w-full h-full object-cover" data-id="element-1259" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20" data-id="element-1260" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-32" data-id="element-1261">
        <nav className="mb-5" aria-label="Breadcrumb" data-id="element-1262">
          <ol className="flex items-center gap-2 text-sm text-white/60" data-id="element-1263">
            <li data-id="element-1264">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-1265">
                Home
              </button>
            </li>
            <li data-id="element-1266">/</li>
            <li data-id="element-1267">
              <button onClick={() => onNavigate('about')} className="hover:text-white transition-colors" data-id="element-1268">
                About
              </button>
            </li>
            <li data-id="element-1269">/</li>
            <li className="text-[#D4A853]" data-id="element-1270">{config.name}</li>
          </ol>
        </nav>

        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6" data-id="element-1271">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6
        }} className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden border-3 border-[#D4A853]/30 shadow-2xl flex-shrink-0" data-id="element-1272">
            <img src={config.photo} alt={config.name} className="w-full h-full object-cover" data-id="element-1273" />
          </motion.div>
          <div data-id="element-1274">
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-1275">
              {config.name}
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="text-[#D4A853] text-lg font-medium mb-2" data-id="element-1276">
              {config.role}
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex flex-wrap items-center gap-4 text-white/60 text-sm" data-id="element-1277">
              <span className="flex items-center gap-1.5" data-id="element-1278">
                <MapPinIcon className="w-3.5 h-3.5" data-id="element-1279" /> {config.location}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-1280">
                <BriefcaseIcon className="w-3.5 h-3.5" data-id="element-1281" />{' '}
                {config.yearsExperience}+ years experience
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}
function GuideBio({
  config
}: {
  config: GuideConfig;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1282">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1283">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16" data-id="element-1284">
          {/* Bio */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} className="lg:col-span-2" data-id="element-1285">
            <div className="flex items-center gap-3 mb-6" data-id="element-1286">
              <span className="text-[#D4A853]/40 text-xs" data-id="element-1287">◆</span>
              <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1288" />
              <span className="text-[#D4A853]/40 text-xs" data-id="element-1289">◆</span>
            </div>
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-1290">
              About {config.name.split(' ')[0]}
            </h2>
            <div className="space-y-5 text-slate-600 leading-relaxed text-base" data-id="element-1291">
              {config.bio.map((paragraph, i) => <p key={i} data-id="element-1292">{paragraph}</p>)}
            </div>
          </motion.div>

          {/* Sidebar — Quick Info */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.15
        }} data-id="element-1293">
            <div className="sticky top-28 space-y-6" data-id="element-1294">
              <div className="bg-white rounded-2xl p-6 border border-[#D4A853]/10" data-id="element-1295">
                <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1296">
                  <LanguagesIcon className="w-5 h-5 text-teal-600" data-id="element-1297" /> Languages
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1298">
                  {config.languages.map(lang => <span key={lang} className="px-3 py-1.5 bg-teal-50 text-teal-700 text-sm font-medium rounded-full" data-id="element-1299">
                      {lang}
                    </span>)}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-[#D4A853]/10" data-id="element-1300">
                <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1301">
                  <CompassIcon className="w-5 h-5 text-teal-600" data-id="element-1302" /> Specialties
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1303">
                  {config.specialties.map(spec => <span key={spec} className="px-3 py-1.5 bg-[#D4A853]/10 text-slate-700 text-sm font-medium rounded-full" data-id="element-1304">
                      {spec}
                    </span>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CompassIcon(props: React.SVGProps<SVGSVGElement> & {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-1305">
      <circle cx="12" cy="12" r="10" data-id="element-1306" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" data-id="element-1307" />
    </svg>;
}
function ExperienceEducation({
  config
}: {
  config: GuideConfig;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-1308">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1309">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16" data-id="element-1310">
          {/* Experience */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} data-id="element-1311">
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3" data-id="element-1312">
              <BriefcaseIcon className="w-6 h-6 text-teal-600" data-id="element-1313" /> Experience
            </h2>
            <div className="space-y-6" data-id="element-1314">
              {config.experience.map((exp, i) => <div key={i} className="relative pl-6 border-l-2 border-[#D4A853]/30" data-id="element-1315">
                  <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[#D4A853]" data-id="element-1316" />
                  <p className="text-sm text-teal-600 font-medium mb-1" data-id="element-1317">
                    {exp.period}
                  </p>
                  <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-1" data-id="element-1318">
                    {exp.role}
                  </h3>
                  <p className="text-slate-500 text-sm font-medium mb-2" data-id="element-1319">
                    {exp.company}
                  </p>
                  <p className="text-slate-600 text-base leading-relaxed" data-id="element-1320">
                    {exp.description}
                  </p>
                </div>)}
            </div>
          </motion.div>

          {/* Education */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.15
        }} data-id="element-1321">
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3" data-id="element-1322">
              <BookOpenIcon className="w-6 h-6 text-teal-600" data-id="element-1323" /> Education
            </h2>
            <div className="space-y-5" data-id="element-1324">
              {config.education.map((edu, i) => <div key={i} className="bg-[#FAF8F5] rounded-xl p-5 border border-[#D4A853]/10" data-id="element-1325">
                  <p className="text-sm text-[#D4A853] font-semibold mb-1" data-id="element-1326">
                    {edu.year}
                  </p>
                  <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-1" data-id="element-1327">
                    {edu.degree}
                  </h3>
                  <p className="text-slate-500 text-sm" data-id="element-1328">{edu.institution}</p>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CertificatesSection({
  config
}: {
  config: GuideConfig;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: i * 0.1,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };
  return <section className="w-full bg-[#0C1F2C] py-20 sm:py-28" ref={ref} data-id="element-1329">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1330">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-14" data-id="element-1331">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1332">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1333">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1334" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1335">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-white mb-3" data-id="element-1336">
            Certificates & Training
          </h2>
          <p className="text-slate-400 font-light text-lg" data-id="element-1337">
            Professional qualifications and certifications
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1338">
          {config.certificates.map((cert, i) => <motion.div key={cert.title} custom={i} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5" data-id="element-1339">
              <div className="relative overflow-hidden aspect-[16/9]" data-id="element-1340">
                <img src={cert.image} alt={cert.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1341" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-1342" />
                <div className="absolute bottom-4 left-4 right-4" data-id="element-1343">
                  <span className="px-2.5 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-1344">
                    {cert.year}
                  </span>
                </div>
              </div>
              <div className="p-6" data-id="element-1345">
                <h3 className="font-playfair text-lg font-semibold text-white mb-1.5" data-id="element-1346">
                  {cert.title}
                </h3>
                <p className="text-slate-400 text-sm" data-id="element-1347">{cert.issuer}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function AchievementsSection({
  config
}: {
  config: GuideConfig;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1348">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1349">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-14" data-id="element-1350">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1351">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1352">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1353" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1354">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-1355">
            Key Achievements
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1356">
          {config.achievements.map((achievement, i) => <motion.div key={achievement.title} initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-white rounded-2xl p-6 sm:p-8 border border-[#D4A853]/10 flex items-start gap-4" data-id="element-1357">
              <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1358">
                <AwardIcon className="w-6 h-6 text-[#D4A853]" data-id="element-1359" />
              </div>
              <div data-id="element-1360">
                <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-1.5" data-id="element-1361">
                  {achievement.title}
                </h3>
                <p className="text-slate-600 text-base leading-relaxed" data-id="element-1362">
                  {achievement.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export function GuidePage({
  guideKey,
  onNavigate
}: GuidePageProps) {
  const config = guides[guideKey];
  if (!config) return null;
  return <div className="w-full" data-id="element-1363">
      <GuideHero config={config} onNavigate={onNavigate} data-id="element-1364" />
      <GuideBio config={config} data-id="element-1365" />
      <ExperienceEducation config={config} data-id="element-1366" />
      <CertificatesSection config={config} data-id="element-1367" />
      <AchievementsSection config={config} data-id="element-1368" />
    </div>;
}